import React, { useState, useEffect, useCallback } from 'react';
import useTabFromUrl from '../../hooks/useTabFromUrl';
import { Sun, BarChart3, ClipboardList, Plug } from 'lucide-react';
import { EmailSignalEngine } from '../../utils/emailSignalData';
import { Button } from '../ui';
import HuddleBrief from './HuddleBrief';
import KPIDashboard from './KPIDashboard';
import InterventionQueue from './InterventionQueue';
import ConnectorSetup from './ConnectorSetup';

const TAB_ICONS = { Sun, BarChart3, ClipboardList, Plug };
const INACTIVE_COLOR = '#8B95A5';
const ACTIVE_COLOR = '#2C5FA8';
const ACTIVE_ACCENT = '#00CED1';
const ICON_SIZE = 18;

export default function EmailSignalDashboard() {
  const [activeTab, setActiveTab] = useTabFromUrl('huddle');
  const [org, setOrg] = useState(null);
  const [queueCount, setQueueCount] = useState(0);

  const loadOrg = useCallback(() => {
    setOrg(EmailSignalEngine.getOrg());
  }, []);

  const loadQueueCount = useCallback(() => {
    setQueueCount(EmailSignalEngine.getInterventionQueue().length);
  }, []);

  useEffect(() => {
    EmailSignalEngine.ensureMockData();
    loadOrg();
    loadQueueCount();
  }, [loadOrg, loadQueueCount]);

  const tabs = [
    { id: 'huddle', label: 'Morning Brief', iconKey: 'Sun' },
    { id: 'dashboard', label: 'KPI Dashboard', iconKey: 'BarChart3' },
    { id: 'queue', label: 'Intervention Queue', iconKey: 'ClipboardList', badge: queueCount },
    { id: 'connector', label: 'Connector', iconKey: 'Plug' },
  ];

  return (
    <div className="dashboard-container">
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 className="dashboard-title">Email Signal Engine</h1>
          <p className="dashboard-subtitle">Making operational strain visible and actionable</p>
        </div>
        {org?.connectorStatus === 'calibrating' && (
          <span className="text-body font-semibold text-yellow-nn" style={{ padding: '8px 16px', background: '#FEF3C7', borderRadius: 8 }}>
            Calibrating (first 14 days)
          </span>
        )}
        {org?.connectorStatus === 'active' && (
          <span className="text-body font-semibold text-green-nn" style={{ padding: '8px 16px', background: '#D1FAE5', borderRadius: 8 }}>
            Active
          </span>
        )}
      </div>

      <nav className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto bg-white rounded-t-lg px-2 pt-2">
        {tabs.map(tab => {
          const Icon = TAB_ICONS[tab.iconKey];
          const isActive = activeTab === tab.id;
          const iconColor = isActive ? ACTIVE_COLOR : INACTIVE_COLOR;
          return (
            <Button
              key={tab.id}
              variant="ghost"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
                isActive ? 'border-blue-600 text-blue-600 font-medium' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
              style={isActive ? { color: ACTIVE_COLOR, borderBottomColor: ACTIVE_ACCENT } : {}}
            >
              <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                {Icon && <Icon size={ICON_SIZE} color={iconColor} style={{ flexShrink: 0 }} />}
                {tab.badge > 0 && (
                  <span
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-caption font-bold rounded-full"
                  >
                    {tab.badge}
                  </span>
                )}
              </span>
              <span style={{ marginLeft: tab.badge > 0 ? 4 : 0 }}>{tab.label}</span>
            </Button>
          );
        })}
      </nav>

      <div className="tab-content">
        {activeTab === 'huddle' && <HuddleBrief />}
        {activeTab === 'dashboard' && <KPIDashboard />}
        {activeTab === 'queue' && <InterventionQueue onResolve={loadQueueCount} onDismiss={loadQueueCount} />}
        {activeTab === 'connector' && <ConnectorSetup onSetup={loadOrg} />}
      </div>
    </div>
  );
}
